import { ActionIcon, Box, Group, Paper, Stack, Text } from '@mantine/core';
import { IconGripVertical, IconX } from '@tabler/icons-react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  type DropResult,
} from '@hello-pangea/dnd';
import type { ReportColumn } from '../types';

interface Props {
  columns: ReportColumn[];
  /** Called with the new array order after a drop. */
  onReorder: (next: ReportColumn[]) => void;
  onRemove: (key: string) => void;
}

/** The chosen columns, in export order. Drag the handle to reorder, X to remove. */
export function SelectedColumnsList({ columns, onReorder, onRemove }: Props) {
  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const from = result.source.index;
    const to = result.destination.index;
    if (from === to) return;

    const next = [...columns];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onReorder(next);
  };

  return (
    <>
      <Text size="xs" c="dimmed" fw={600} tt="uppercase" mb={4}>
        Drag items to set column order.
      </Text>

      {columns.length === 0 ? (
        <Text size="sm" c="dimmed">No columns selected.</Text>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="selected-columns">
            {(provided) => (
              <Stack gap={4} ref={provided.innerRef} {...provided.droppableProps}>
                {columns.map((c, i) => (
                  <Draggable key={c.key} draggableId={c.key} index={i}>
                    {(dragProvided, snapshot) => (
                      <Paper
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        withBorder
                        px="xs"
                        py={4}
                        radius="sm"
                        style={{
                          ...dragProvided.draggableProps.style,
                          background: snapshot.isDragging
                            ? 'var(--mantine-color-blue-light)'
                            : undefined,
                        }}
                      >
                        <Group justify="space-between" wrap="nowrap">
                          <Group gap={6} wrap="nowrap">
                            <Box
                              {...dragProvided.dragHandleProps}
                              aria-label={`Reorder ${c.label}`}
                              style={{ display: 'flex', cursor: 'grab', color: 'var(--mantine-color-dimmed)' }}
                            >
                              <IconGripVertical size={14} />
                            </Box>
                            <Text size="sm">{c.label}</Text>
                          </Group>
                          <ActionIcon
                            size="sm"
                            variant="subtle"
                            color="red"
                            onClick={() => onRemove(c.key)}
                            aria-label={`Remove ${c.label}`}
                          >
                            <IconX size={14} />
                          </ActionIcon>
                        </Group>
                      </Paper>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Stack>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </>
  );
}